import { useEffect, useRef, useState } from 'react'

export type AsyncRead<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; data: T }
  | { status: 'error'; error: unknown }

/**
 * One read owned by the current `key` (the selection it answers for, e.g. a
 * ticket's zone ids). A null key disables the read. The read restarts, and old
 * data/errors are hidden on the first render, ONLY when the key changes: the
 * loader is held in a ref, so an unstable loader (an inline authHeaders, an
 * un-memoised callback) can neither loop the render nor blank the content.
 * Put every input that should refetch into the key. Cleanup ignores late
 * settlement, even when the transport cannot be aborted. Keep editable
 * children mounted while rendering these states. This is not a mutation queue
 * or a cache; side effects inside load remain the caller's own.
 */
export function useAsyncRead<T>(key: string | null, load: () => Promise<T>): AsyncRead<T> {
  const loadRef = useRef(load)
  loadRef.current = load
  const [settled, setSettled] = useState<{
    key: string | null; state: AsyncRead<T>
  }>({ key, state: key !== null ? { status: 'loading' } : { status: 'idle' } })

  useEffect(() => {
    if (key === null) return
    let stale = false
    void (async () => {
      try {
        const data = await loadRef.current()
        if (!stale) setSettled({ key, state: { status: 'success', data } })
      } catch (error) {
        if (!stale) setSettled({ key, state: { status: 'error', error } })
      }
    })()
    return () => { stale = true }
  }, [key])

  if (settled.key !== key) {
    const state: AsyncRead<T> = key !== null ? { status: 'loading' } : { status: 'idle' }
    // Reset during render so even layout effects cannot see the previous key.
    // Recording null also prevents re-enabling the same key reviving data.
    // Keyed on a string, so this runs once per key change and cannot loop.
    setSettled({ key, state })
    return state
  }
  return settled.state
}
